// src/components/OpportunitesModal.tsx
import React, { FC, useState, useEffect } from 'react';
import { db } from "../firebase";
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import '../styles/OpportunitesModal.css';
import { toast } from "react-toastify";

interface FirestoreOpportunity {
  companyId: string;
  contact: string;
  type: string;
  amount: number;
  date: string;
  createdAt?: string;
  qte?: number;
  pu?: number;
  remise?: number;
  ttc?: number;
  description?: string;
  status?: string;
  tva?: number;
}

interface Company {
  id: string;
  name: string;
  contacts: string[];
}

interface OpportunitesModalProps {
  show: boolean;
  handleClose: () => void;
  onSave: (data: FirestoreOpportunity) => void;
  onUpdate: (oldOpp: FirestoreOpportunity, newOpp: FirestoreOpportunity) => void;
  editingOpportunity: FirestoreOpportunity | null;
  opportunityTypes?: string[]; // Types dynamiques passés depuis la page parente
  preselectedCompanyId?: string | null; // Société pré-sélectionnée depuis la navigation
}

const OpportunitesModal: FC<OpportunitesModalProps> = ({
  show,
  handleClose,
  onSave,
  onUpdate,
  editingOpportunity,
  opportunityTypes = [],
  preselectedCompanyId
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedContact, setSelectedContact] = useState('');
  const [opportunityType, setOpportunityType] = useState('');
  const [qte, setQte] = useState<string>('1');
  const [puHT, setPuHT] = useState<number>(0);
  const [remise, setRemise] = useState<number>(0);
  const [opportunityDate, setOpportunityDate] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('En Cours');
  const [tva, setTva] = useState<number>(20);
  const [tvaIsCustom, setTvaIsCustom] = useState(false);

  const isEditMode = editingOpportunity !== null;
  const budgetHT = Number(qte.replace(',', '.')) * puHT * (1 - (remise || 0) / 100);
  const budgetTTC = budgetHT * (1 + tva / 100);

  const resetForm = () => {
    setSelectedCompanyId('');
    setSelectedContact('');
    setOpportunityType('');
    setQte('1');
    setPuHT(0);
    setRemise(0);
    setOpportunityDate('');
    setDescription('');
    setStatus('En Cours');
    setTva(20);
    setTvaIsCustom(false);
  };

  useEffect(() => {
    if (show) {
      fetchCompanies();
    }
  }, [show]);

  // Pré-sélectionner la société si elle est passée en prop
  useEffect(() => {
    if (show && preselectedCompanyId && !editingOpportunity && companies.length > 0) {
      // Chercher la société par id ou par name (car la clé peut être différente)
      const foundCompany = companies.find(c => 
        c.id === preselectedCompanyId || 
        c.name === preselectedCompanyId ||
        c.id.replace(/[./]/g, "_") === preselectedCompanyId.replace(/[./]/g, "_") ||
        c.name.replace(/[./]/g, "_") === preselectedCompanyId.replace(/[./]/g, "_")
      );
      
      if (foundCompany) {
        setSelectedCompanyId(foundCompany.id);
        // Sélectionner automatiquement le premier contact s'il existe
        if (foundCompany.contacts && foundCompany.contacts.length > 0) {
          setSelectedContact(foundCompany.contacts[0]);
        }
      }
    }
  }, [show, preselectedCompanyId, editingOpportunity, companies]);

  useEffect(() => {
    if (editingOpportunity) {
      setSelectedCompanyId(editingOpportunity.companyId);
      setSelectedContact(editingOpportunity.contact);
      setOpportunityType(editingOpportunity.type);
      setOpportunityDate(editingOpportunity.date);
      setQte(editingOpportunity.qte?.toString() || '1');
      setPuHT(editingOpportunity.pu || 0);
      setRemise(editingOpportunity.remise || 0);
      setDescription(editingOpportunity.description || '');
      setStatus(editingOpportunity.status || 'En Cours');
      const tvaValue = editingOpportunity.tva !== undefined ? editingOpportunity.tva : 20;
      setTva(tvaValue);
      setTvaIsCustom(tvaValue !== 20);
    } else {
      resetForm();
      setRemise(0);
    }
  }, [editingOpportunity]);

  const fetchCompanies = async () => {
    try {
      const docRef = doc(db, 'prospects', 'companies');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const dataObj = docSnap.data() as Record<string, any>;
        const parsedCompanies: Company[] = Object.entries(dataObj).map(([companyName, value]) => {
          const contactsArr: string[] = [];
  
          // Contact principal avec civilité
          if (value.principalContact) {
            const fullName = `${value.principalGender || "Mr"} ${value.principalFirstName || ""} ${value.principalContact}`.trim();
            contactsArr.push(fullName);
          }
  
          // Autres contacts avec civilité
          if (Array.isArray(value.otherContacts)) {
            value.otherContacts.forEach((c: any) => {
              const fullName = `${c.civilite || "Mr"} ${c.prenom || ""} ${c.nom || ""}`.trim();
              contactsArr.push(fullName);
            });
          }
  
          return {
            id: companyName,
            name: companyName,
            contacts: contactsArr
          };
        }).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  
        setCompanies(parsedCompanies);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des sociétés :', error);
    }
  };
  

  const filteredContacts = companies.find((c) => c.id === selectedCompanyId)?.contacts || [];

  const handleCompanyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCompanyId(event.target.value);
    setSelectedContact('');
  };

  const handleQteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Permettre uniquement les chiffres, un point ou une virgule
    if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
      setQte(value || '1');
    }
  };

  const handleAddOpportunity = async () => {
    if (!selectedCompanyId || !selectedContact || !opportunityType || !budgetHT || !opportunityDate) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }

    const opportunityData: FirestoreOpportunity = {
      companyId: selectedCompanyId,
      contact: selectedContact,
      type: opportunityType,
      amount: Number(budgetHT),
      remise,
      date: opportunityDate,
      createdAt: new Date().toISOString(),
      qte: Number(qte.replace(',', '.')),
      pu: puHT,
      ttc: Number(budgetTTC.toFixed(2)),
      description,
      status,
      tva,
    };

    try {
      const docRef = doc(db, 'prospects', 'companies');
      await updateDoc(docRef, {
        [selectedCompanyId + '.opportunities']: arrayUnion(opportunityData)
      });

      toast.success("Demande ajoutée avec succès !");
      onSave(opportunityData);
      resetForm();
      handleClose();
    } catch (error) {
      console.error('Erreur lors de la création de la demande :', error);
      toast.error("Une erreur est survenue.");
    }
  };

  const handleUpdateOpportunity = async () => {
    if (!editingOpportunity) return;
    if (!selectedCompanyId || !selectedContact || !opportunityType || !budgetHT || !opportunityDate) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }

    const newOpportunity: FirestoreOpportunity = {
      companyId: selectedCompanyId,
      contact: selectedContact,
      type: opportunityType,
      amount: Number(budgetHT),
      remise,
      date: opportunityDate,
      createdAt: editingOpportunity.createdAt || new Date().toISOString(),
      qte: Number(qte.replace(',', '.')),
      pu: puHT,
      ttc: Number(budgetTTC.toFixed(2)),
      description,
      status,
      tva,
    };

    try {
      const docRef = doc(db, 'prospects', 'companies');
      await updateDoc(docRef, {
        [editingOpportunity.companyId + '.opportunities']: arrayRemove(editingOpportunity)
      });
      await updateDoc(docRef, {
        [newOpportunity.companyId + '.opportunities']: arrayUnion(newOpportunity)
      });

      toast.success("Demande mise à jour avec succès !");
      onUpdate(editingOpportunity, newOpportunity);
      resetForm();
      handleClose();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la demande :', error);
      toast.error("Une erreur est survenue.");
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{isEditMode ? "Modifier la Demande" : "Ajouter une Demande"}</h2>

        {/* Société */}
        <div className="form-group">
          <label htmlFor="companySelect">Société :</label>
          <select id="companySelect" value={selectedCompanyId} onChange={handleCompanyChange}>
            <option value="" disabled>-- Sélectionnez une société --</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        {/* Contact */}
        <div className="form-group">
          <label htmlFor="contactSelect">Contact :</label>
          <select id="contactSelect" value={selectedContact} onChange={(e) => setSelectedContact(e.target.value)}>
            <option value="" disabled>-- Sélectionnez un contact --</option>
            {filteredContacts.map((contact) => (
              <option key={contact} value={contact}>
                {contact}
              </option>
            ))}
          </select>
        </div>

        {/* Type */}
        <div className="form-group">
          <label htmlFor="typeSelect">Type de demande :</label>
          <select id="typeSelect" value={opportunityType} onChange={(e) => setOpportunityType(e.target.value)}>
            <option value="">-- Sélectionnez un type --</option>
            {opportunityTypes.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Description */}
<div className="form-group">
  <label htmlFor="descriptionInput">Description :</label>
  <textarea
    id="descriptionInput"
    value={description}
    onChange={(e) => setDescription(e.target.value)}
    rows={3}
    placeholder="Ajouter une description (optionnel)"
  />
</div>


        {/* Qté, PU HT, Budget HT */}
        <div className="form-row-3">
          <div className="form-group">
            <label>Quantité :</label>
            <input
              type="text"
              value={qte}
              onChange={handleQteChange}
              placeholder="1"
              inputMode="decimal"
            />
          </div>
          <div className="form-group">
            <label>Prix Unitaire (HT) :</label>
            <input
              type="number"
              value={puHT}
              onChange={(e) => setPuHT(Number(e.target.value))}
              min={0}
            />
          </div>
          <div className="form-group">
            <label>Remise (%) :</label>
            <input
              type="number"
              value={remise}
              onChange={(e) => setRemise(Number(e.target.value))}
              min={0}
              max={100}
            />
          </div>
        </div>

        <div className="form-row-2">
          <div className="form-group">
            <label>Montant (HT) :</label>
            <input
              type="number"
              value={budgetHT.toFixed(2)}
              readOnly
            />
          </div>
          <div className="form-group">
            <label>Montant (TTC) :</label>
            <input
              type="number"
              value={budgetTTC.toFixed(2)}
              readOnly
              style={{ color: 'green', fontWeight: 'bold' }}
            />
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={tvaIsCustom}
              onChange={(e) => {
                const checked = e.target.checked;
                setTvaIsCustom(checked);
                if (!checked) setTva(20);
              }}
              style={{ width: 'auto', cursor: 'pointer' }}
            />
            TVA différente de 20%
          </label>
          {tvaIsCustom && (
            <input
              type="number"
              value={tva}
              onChange={(e) => setTva(parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              step="0.1"
              placeholder="Ex: 0"
              style={{ marginTop: '8px' }}
            />
          )}
          {!tvaIsCustom && (
            <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>TVA appliquée : 20%</small>
          )}
          {tvaIsCustom && tva === 0 && (
            <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>TVA 0% — Montant TTC = HT : {budgetHT.toFixed(2)}€</small>
          )}
        </div>

        {/* Date */}
        <div className="form-group">
          <label htmlFor="dateInput">Date de la demande :</label>
          <input
            id="dateInput"
            type="date"
            value={opportunityDate}
            onChange={(e) => setOpportunityDate(e.target.value)}
          />
        </div>

        {/* Statut */}
        <div className="form-group">
          <label htmlFor="statusSelect">Statut :</label>
          <select id="statusSelect" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="En Cours">En Cours</option>
            <option value="Validé">Validé</option>
            <option value="Annulé">Annulé</option>
          </select>
        </div>

        <div className="modal-actions">
          <button type="submit" onClick={isEditMode ? handleUpdateOpportunity : handleAddOpportunity}>
            {isEditMode ? "Enregistrer" : "Ajouter"}
          </button>
          <button type="button" onClick={() => { resetForm(); handleClose(); }}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpportunitesModal;
